import { type ReactElement, type ReactNode } from 'react';

import type { Product } from '@atlassian/teams-common/types';

import type { Flag, Team, TeamMembershipSettings } from '../../types';
import type { InvitedUser } from '../../types/user';
import type { ErrorWithStatus } from '../../utils/fetch';
import type { MemberPickerProps } from '../MemberPicker/types';
import type { customInputFieldProps } from '../TeamCreate/types';

export type AnalyticsEventAttributes = { [key: string]: any };

interface PrepopulatedFields {
	/**
	 * Set a initial team name when opening dialog
	 */
	teamName?: string;
	/** account identifiers of the proposed members of the team (can be edited by the user). Can be optional because of the way this dialog is instantiated */
	members?: InvitedUser[];
	/**
	 * Which membership settings should the dialog have selected initially. The
	 * two types are open and invite-only.
	 *
	 * The user can then choose to change this.
	 *
	 * If no value is provided, the dialog will default to open.
	 *
	 * If you have no preference, it is best to leave this prop undefined, so
	 * that People & Teams can control which is the initial team type.
	 */
	membershipSettings?: TeamMembershipSettings;
}

export interface TeamCreateDialogBaseProps {
	// cloudId
	cloudId: string;
	// Organization ID. Teams are now organization-based, so this component will not work without an orgId provided.
	orgId?: string;
	// product name
	product: Product;
	/**
	 * @deprecated use prepopulatedFields.members instead, prepopulatedFields values will override this property if set
	 */
	/** account identifiers of the proposed members of the team (can be edited by the user). Can be optional because of the way this dialog is instantiated */
	proposedMembers?: InvitedUser[];
	/**
	 * @deprecated no longer used
	 * User Id of current logged-in user
	 */
	principalId?: string;
	// maximum number of members in member select
	maxSelectedMembers?: number;
	// A callback when the dialog is closed
	onClose?: () => void;
	// Disable showing success flag after creating team successfully
	disableSuccessFlag?: boolean;
	// allow to add extra analytics events
	extraAnalyticsAttrs?: AnalyticsEventAttributes;
	// Disable showing the modal dialog blanket
	isBlanketHidden?: boolean;
	/**
	 * @deprecated use prepopulatedFields.membershipSettings instead, prepopulatedFields values will override this property if set
	 */
	/**
	 * Which membership settings should the dialog have selected initially. The
	 * two types are open and invite-only.
	 *
	 * The user can then choose to change this.
	 *
	 * If no value is provided, the dialog will default to open.
	 *
	 * If you have no preference, it is best to leave this prop undefined, so
	 * that People & Teams can control which is the initial team type.
	 */
	initialMembershipSettings?: TeamMembershipSettings;
	/**
	 * This is intended to accept a feature flag value, used for the roll-out
	 * of this new feature.
	 */
	enableMembershipSettingsChoice?: boolean;
	/**
	 * Allow info about the loading state to bubble out of component.
	 * A call will be made with true when the team creation process starts,
	 * and a call will be made with false when it finishes.
	 */
	setIsLoading?: (isLoading: boolean) => void;
	/**
	 * Allow custom messages to replace our default ones
	 */
	customMessages?: {
		modalTitle?: ReactNode;
		submitButtonLabel?: ReactNode;
		descriptionContent?: ReactNode;
		imageContent?: ReactNode;
	};
	/**
	 * The proportion that the image takes up for the content row
	 * Allow to customise the flex ratio between the image content and description content
	 */
	imageProportion?: number;
	/**
	 * The proportion that the description takes up for the content row
	 * Allow to customise the flex ratio between the image content and description content
	 */
	descriptionProportion?: number;
	/**
	 * Allow custom components (i.e. ModalTitle, Modalfooter etc.) to replace default ones
	 * help bridge up old and new modaldialog versions
	 */
	customComponents?: {
		modalHeader?: () => ReactElement;
		modalTitle?: () => ReactElement;
		modalBody?: () => ReactElement;
		modalFooter?: () => ReactElement;
		memberPicker?: ({ ...props }: MemberPickerProps) => ReactElement;
		customInputField?: ({ ...props }: customInputFieldProps) => ReactElement;
	};
	/**
	 * Identity of current logged-in user
	 * Only if this property is provided current user gets added to members of the team
	 */
	currentUser?: InvitedUser;

	/**
	 * A flag to enable the feature "Team permission check"
	 */
	enableTeamPermissionCheck?: boolean;

	/**
	 * All prepopulate-able fields
	 */
	prepopulatedFields?: PrepopulatedFields;

	/**
	 * An otpional prop to show refreshed form
	 */
	enableNewFormUI?: boolean;
}

export interface TeamCreateDialogInternalProps extends TeamCreateDialogBaseProps {
	// A callback to create team data
	createTeamAndAddMembers: (
		teamName: string,
		selectedMembers: InvitedUser[],
		membershipSettings: TeamMembershipSettings,
	) => void;
	// A boolean to control state of creating team dialog
	isFetching?: boolean;
}

export interface TeamCreateDialogProps extends TeamCreateDialogBaseProps {
	// A callback when creating team successfully. Consumers can hook on this to show a flag
	onCreateTeamSuccess?: (
		teamData: Team,
		invitedMembers: InvitedUser[],
		failedInvitedMembers?: InvitedUser[],
	) => void;
	// A callback when creating team failed. Consumers can hook on this to show a flag
	onCreateTeamFailed?: (error: ErrorWithStatus, invitedMembers: InvitedUser[]) => void;
	/**
	 * A callback is used to render flag inside product.
	 * For example, we want to show a error flag when creating team failed
	 */
	addFlag?: (flag: Flag) => void;
	/**
	 * A push function of router is used to redirect to people page or team page or search people/teams home page
	 */
	pushRoute?: (path: string) => void;
}

export type ImageSectionProps = {
	/**
	 * The proportion that the image takes up for the content row
	 * Allow to customise the flex ratio between the image content and description content
	 */
	imageProportion?: number;

	wide?: boolean;
};

export type TeamDataSectionProps = {
	/**
	 * The proportion that the description takes up for the content row
	 * Allow to customise the flex ratio between the image content and description content
	 */
	descriptionProportion?: number;

	wide?: boolean;
};

export enum ValidationError {
	/**
	 * Error type when the required field not filled
	 */
	REQUIRED = 'required',
	EXCEED_MAX_LENGTH = 'exceed_max_length',
}
